// SettingsPlanPane.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatSubscriptionCancelConfirmMessage } from '../utils/settingsPlanPaneUtils';

// Hooks
import useSettingsPlanPaneState from '../hooks/useSettingsPlanPaneState';
import useSettingsPlanPaneViewModel from '../hooks/useSettingsPlanPaneViewModel';
import useBaseEntitlements from 'hooks/useBaseEntitlements';
import useBaseSeatLimit from 'hooks/useBaseSeatLimit';

// CSS
import './SettingsPlanPane.css';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import PlanCheckoutModal from './PlanCheckoutModal';
import SettingsPlanCatalogGrid from './SettingsPlanCatalogGrid';
import SettingsPlanOverviewCards from './SettingsPlanOverviewCards';
import SettingsPlanThresholdAlerts from './SettingsPlanThresholdAlerts';
import Confirm from 'components/confirm/Confirm';

/**
 * SettingsPlanPane
 *
 * Renders the space plan catalog and allows self-serve selection for
 * public free plans.
 *
 * @param {object} user - Active user model.
 * @param {object} base - Active base model.
 * @param {boolean} canEditBase - Whether the current user can edit space settings.
 *
 * @returns {ReactNode} The rendered plan settings pane.
 */
const SettingsPlanPane = React.memo(({
    user,
    base,
    canEditBase,
    preferredPlanKey = '',
    productLineTitle = '',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();
    const lineTitle = String(productLineTitle || '').trim() || 'this base';

    // ----- HOOKS ----- //

    const {
        planState,
        billingState,
        usageState,
        messageState,
        handlers,
    } = useSettingsPlanPaneState({
        user,
        base,
        canEditBase,
        preferredPlanKey,
    });

    const {
        plans,
        isLoading,
        savingPlanKey,
        currentPlanKey,
        currentPlan,
        currentPlanTitle,
        isCurrentPlanHiddenFromCatalog,
        checkoutPlan,
        downgradePlanKey,
    } = planState;
    const {
        billingRecord,
        billingLoading,
        billingSubscriptionId,
        hasActivePaidSubscription,
        isCancelSubscriptionConfirming,
        canCancelPaidSubscription,
        isOpeningBillingPage,
        canOpenBillingPage,
    } = billingState;
    const {
        apiLimits,
        apiUsageLoading,
        apiUsageError,
        liveUsage,
        liveUsageLoading,
        liveUsageError,
    } = usageState;
    const {
        errorMessage,
        statusMessage,
    } = messageState;
    const {
        handleSelectPlan,
        handleStartPaidCheckout,
        handleCancelPaidCheckout,
        handleStartCancelSubscription,
        handleStartDowngradeToPlan,
        handleAbortCancelSubscription,
        handleCancelSubscription,
        handleOpenBillingPage,
        handlePaidCheckoutSuccess,
    } = handlers;

    const {
        pendingInviteCount,
        reservedSeatCount,
    } = useBaseSeatLimit({
        canListPendingInvites: canEditBase,
        isActive: Boolean(base?.key),
        memberCount: 0,
        planKey: currentPlanKey,
        baseKey: base?.key,
        baseMemberCount: base?.memberCount,
    });

    const billedSeatCount = Math.max(1, reservedSeatCount);

    const {
        entitlements: baseEntitlements,
        loading: entitlementsLoading,
        error: entitlementsError,
    } = useBaseEntitlements({
        isActive: Boolean(base?.key),
        baseKey: base?.key,
    });

    const {
        apiUsageSummary,
        billingCancellationNotice,
        billingMonthlyTotalText,
        billingPeriodEndLabel,
        billingRenewsText,
        currentPlanPriceText,
        liveUsageSummary,
        membersUsageText,
        shouldShowApiUsage,
        shouldShowLiveUsage,
        shouldShowRecordingUsage,
        storagePerSeatNote,
        storageReservedNote,
        storageUsageText,
        thresholdAlerts,
        usageAllowanceSummary,
    } = useSettingsPlanPaneViewModel({
        apiLimits,
        billedSeatCount,
        billingLoading,
        billingRecord,
        currentPlan,
        entitlementsError,
        entitlementsLoading,
        isCurrentPlanHiddenFromCatalog,
        liveUsage,
        pendingInviteCount,
        usageAllowance: baseEntitlements?.usageAllowance || null,
        base,
    });

    // ----- MEMOS ----- //

    const downgradePlan = useMemo(() => {
        if (!downgradePlanKey) return null;
        return plans.find((plan) => String(plan?.key || '').trim() === downgradePlanKey) || null;
    }, [downgradePlanKey, plans]);

    const isPlanChangeConfirmOpen = Boolean(isCancelSubscriptionConfirming && billingSubscriptionId);
    const isDowngradeConfirm = Boolean(downgradePlanKey);
    const downgradePlanTitle = String(downgradePlan?.title || 'the free plan').trim();

    const planChangeConfirmTitle = isDowngradeConfirm
        ? `Switch to ${downgradePlanTitle}?`
        : 'Cancel Subscription';

    const planChangeConfirmMessage = formatSubscriptionCancelConfirmMessage({
        downgradePlanTitle,
        isDowngradeConfirm,
        periodEnd: billingRecord?.currentPeriodEnd,
    });

    // ----- HANDLERS ----- //

    /**
     * handleClosePlanChangeConfirm
     *
     * Handles close plan change confirm.
     *
     * @param {boolean} isOpen - isOpen.
     */
    const handleClosePlanChangeConfirm = (isOpen) => {
        if (!isOpen) {
            handleAbortCancelSubscription();
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <SettingsModalPaneHeader
              title="Current Plan"
              subtitle={`Compare plans and upgrade ${lineTitle}, or review current usage below.`}
            />

            <SettingsModalPaneContents>

                {/* PLAN STACK */}
                <div className="settings-modal-plan-stack">

                    {statusMessage && !errorMessage && (
                        <div className="settings-modal-status">{statusMessage}</div>
                    )}

                    {errorMessage && (
                        <div className="settings-modal-error">{errorMessage}</div>
                    )}

                    {!canEditBase && (
                        <div
                          className="settings-modal-note"
                          style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            {`Only admins and owners can change the ${lineTitle} plan.`}
                        </div>
                    )}

                    {isLoading && (
                        <div
                          className="settings-modal-note"
                          style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Loading public plans...
                        </div>
                    )}

                    {!isLoading && !plans.length && !errorMessage && (
                        <div
                          className="settings-modal-note"
                          style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            No public plans are available right now.
                        </div>
                    )}

                    {!isLoading && plans.length > 0 && (
                        <>
                            <SettingsPlanCatalogGrid
                              canEditBase={canEditBase}
                              currentPlan={currentPlan}
                              currentPlanKey={currentPlanKey}
                              hasActivePaidSubscription={hasActivePaidSubscription}
                              isSubscriptionCancelScheduled={Boolean(billingRecord?.cancelAtPeriodEnd)}
                              plans={plans}
                              savingPlanKey={savingPlanKey}
                              theme={theme}
                              onSelectPlan={handleSelectPlan}
                              onStartDowngradeToPlan={handleStartDowngradeToPlan}
                              onStartPaidCheckout={handleStartPaidCheckout}
                            />
                        </>
                    )}

                    {canEditBase ? (
                        <SettingsPlanThresholdAlerts
                          alerts={thresholdAlerts}
                          theme={theme}
                        />
                    ) : null}

                    <SettingsPlanOverviewCards
                      apiUsageError={apiUsageError}
                      apiUsageLoading={apiUsageLoading}
                      apiUsageSummary={apiUsageSummary}
                      billingCancellationNotice={billingCancellationNotice}
                      billingMonthlyTotalText={billingMonthlyTotalText}
                      billingPeriodEndLabel={billingPeriodEndLabel}
                      billingRecord={billingRecord}
                      billingRenewsText={billingRenewsText}
                      billingSubscriptionId={billingSubscriptionId}
                      canCancelPaidSubscription={canCancelPaidSubscription}
                      canOpenBillingPage={canOpenBillingPage}
                      currentPlan={currentPlan}
                      currentPlanPriceText={currentPlanPriceText}
                      currentPlanTitle={currentPlanTitle}
                      isCancelSubscriptionConfirming={isCancelSubscriptionConfirming}
                      isCurrentPlanHiddenFromCatalog={isCurrentPlanHiddenFromCatalog}
                      isOpeningBillingPage={isOpeningBillingPage}
                      liveUsageError={liveUsageError}
                      liveUsageLoading={liveUsageLoading}
                      liveUsageSummary={liveUsageSummary}
                      membersUsageText={membersUsageText}
                      shouldShowApiUsage={shouldShowApiUsage}
                      shouldShowLiveUsage={shouldShowLiveUsage}
                      shouldShowRecordingUsage={shouldShowRecordingUsage}
                      storagePerSeatNote={storagePerSeatNote}
                      storageReservedNote={storageReservedNote}
                      storageUsageText={storageUsageText}
                      theme={theme}
                      usageAllowanceSummary={usageAllowanceSummary}
                      base={base}
                      onOpenBillingPage={handleOpenBillingPage}
                      onStartCancelSubscription={handleStartCancelSubscription}
                    />

                    {/* MEMBER USAGE PANEL 
                    <SettingsPlanMemberUsagePanel
                        canView={canEditBase}
                        theme={theme}
                        base={base}
                    />
                    */}
                </div>
            </SettingsModalPaneContents>

            <PlanCheckoutModal
              isOpen={Boolean(checkoutPlan)}
              billingRecord={billingRecord}
              plan={checkoutPlan}
              user={user}
              base={base}
              onClose={handleCancelPaidCheckout}
              onSuccess={handlePaidCheckoutSuccess}
            />

            <Confirm
              title={planChangeConfirmTitle}
              message={planChangeConfirmMessage}
              isOpen={isPlanChangeConfirmOpen}
              setOpen={handleClosePlanChangeConfirm}
              onConfirm={handleCancelSubscription}
            />
        </>
    );
});

// Dev Tools
SettingsPlanPane.displayName = 'SettingsPlanPane';

export default SettingsPlanPane;
